// src/components/store/sections/product-cards/HexagonPrismProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Check, Hexagon } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const PRISM_THEMES = [
  {
    hexGrad: "from-cyan-500 via-blue-600 to-indigo-700",
    glow: "shadow-cyan-500/20",
    accentText: "text-cyan-600",
    badgeBg: "bg-cyan-500",
  },
  {
    hexGrad: "from-fuchsia-500 via-purple-600 to-indigo-700",
    glow: "shadow-fuchsia-500/20",
    accentText: "text-fuchsia-600",
    badgeBg: "bg-fuchsia-500",
  },
  {
    hexGrad: "from-emerald-500 via-teal-600 to-cyan-700",
    glow: "shadow-emerald-500/20",
    accentText: "text-emerald-600",
    badgeBg: "bg-emerald-500",
  },
  {
    hexGrad: "from-amber-500 via-orange-600 to-rose-600",
    glow: "shadow-orange-500/20",
    accentText: "text-orange-600",
    badgeBg: "bg-orange-500",
  },
];

export default function HexagonPrismProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    discountPercent,
    added,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = PRISM_THEMES[index % PRISM_THEMES.length];

  return (
    <div
      className={`w-full h-full bg-white rounded-[28px] border-2 border-slate-100 p-4 shadow-lg ${theme.glow} hover:shadow-2xl transition-all duration-300 flex flex-col justify-between items-center text-center relative overflow-hidden group select-none`}
    >
      {/* Top Floating Discount Hexagon */}
      <div
        className={`absolute top-3 right-3 z-20 w-11 h-11 bg-gradient-to-br ${theme.hexGrad} text-white flex flex-col items-center justify-center shadow-md`}
        style={{ clipPath: "polygon(50% 0%, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)" }}
      >
        <span className="text-[9px] font-black leading-none">-{discountPercent}%</span>
      </div>

      {/* Hexagonal 3D Prism Image Stage */}
      <div className="relative w-44 h-44 my-1 flex items-center justify-center">
        <div
          className={`w-full h-full bg-gradient-to-br ${theme.hexGrad} p-1.5 transition-transform duration-500 group-hover:rotate-3 group-hover:scale-105 shadow-xl`}
          style={{ clipPath: "polygon(50% 0%, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)" }}
        >
          <div
            className="w-full h-full bg-slate-950 flex items-center justify-center p-4 relative"
            style={{ clipPath: "polygon(50% 0%, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)" }}
          >
            <Link href={productUrl} className="relative w-28 h-28 block">
              <Image
                src={productImg}
                alt={productName}
                fill
                unoptimized
                className="object-contain drop-shadow-[0_10px_15px_rgba(255,255,255,0.25)]"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Info Block */}
      <div className="w-full space-y-1.5 mt-2">
        <span className={`text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1 ${theme.accentText}`}>
          <Hexagon className="w-3 h-3 fill-current opacity-30" /> {categoryName}
        </span>

        <Link href={productUrl} className="block">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight truncate hover:underline">
            {productName}
          </h3>
        </Link>

        <div className="flex items-center justify-center gap-1 text-amber-400">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className="w-3 h-3 fill-amber-400" />
          ))}
          <span className="text-[10px] text-slate-400 font-bold">({rating.toFixed(1)})</span>
        </div>

        <div className="text-xl font-black text-slate-900 font-mono pt-1">
          ৳{currentPrice.toLocaleString()}
        </div>
      </div>

      {/* Hexagon-Clipped Action Button */}
      <div className="w-full pt-3">
        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`w-full py-2.5 px-4 bg-gradient-to-r ${theme.hexGrad} text-white font-black text-[11px] uppercase tracking-wider shadow-md hover:opacity-95 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5`}
          style={{ clipPath: "polygon(6% 0%, 94% 0%, 100% 50%, 94% 100%, 6% 100%, 0% 50%)" }}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
          <span>{added ? "ADDED TO PRISM" : "ADD TO CART"}</span>
        </button>
      </div>
    </div>
  );
}
